// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ExternalUser,
  ExternalUserResponse,
  externalUserResponseSchema,
  externalUsersResponseSchema,
} from '@/account/api/external-user-types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { MessageType } from '@/constants/message-type';
import { NotificationChannel } from '@/constants/notification-channel';

/** Which channels an external user is contacted on for one message type. */
export interface NotificationConfigurationInput {
  messageType: MessageType;
  activeChannels: NotificationChannel[];
}

export interface CreateExternalUserParameters {
  firstName?: string | null;
  lastName?: string | null;
  label: string;
  email: string;
  notifications?: { updates: NotificationConfigurationInput[] };
}

export interface UpdateExternalUserParameters {
  firstName?: string | null;
  lastName?: string | null;
  label?: string;
  email?: string;
  enabled?: boolean;
  notifications?: { updates: NotificationConfigurationInput[] };
}

export async function listExternalUsers(
  organizationKey: string
): Promise<ExternalUser[]> {
  const url = `/v1/organization/${organizationKey}/external-user`;
  const response = await api.get(url);
  const result = await parseResponse(
    externalUsersResponseSchema,
    response.data,
    {
      schemaName: 'ExternalUsers',
      endpoint: url,
    }
  );
  return result.data;
}

export async function createExternalUser(
  organizationKey: string,
  parameters: CreateExternalUserParameters
): Promise<ExternalUserResponse> {
  const url = `/v1/organization/${organizationKey}/external-user`;
  const response = await api.post(url, parameters);
  const result = await parseResponse(
    externalUserResponseSchema,
    response.data,
    {
      schemaName: 'CreateExternalUser',
      endpoint: url,
    }
  );
  return result;
}

export async function updateExternalUser(
  organizationKey: string,
  externalUserKey: string,
  parameters: UpdateExternalUserParameters
): Promise<ExternalUserResponse> {
  const url = `/v1/organization/${organizationKey}/external-user/${externalUserKey}`;
  const response = await api.patch(url, parameters);
  const result = await parseResponse(
    externalUserResponseSchema,
    response.data,
    {
      schemaName: 'UpdateExternalUser',
      endpoint: url,
    }
  );
  return result;
}

export async function deleteExternalUser(
  organizationKey: string,
  externalUserKey: string
): Promise<void> {
  await api.delete(
    `/v1/organization/${organizationKey}/external-user/${externalUserKey}`
  );
}
